import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useOrganization } from '@/hooks/useOrganization';
import { usePhonePlansByOrganizationId } from '@/hooks/usePlans';
import { createColumns } from '@/views/plan-prices/columns';
import { DataTable } from '@/views/plan-prices/data-table';
import { Smartphone } from 'lucide-react';
import { useParams } from 'react-router-dom';

export function PlanPricesView() {
  const { organizationId } = useParams();

  if (!organizationId) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <p className="text-red-500 text-lg">ID de l'organisation non trouvé</p>
      </div>
    );
  }

  return <PlanPricesViewContent organizationId={organizationId} />;
}

function PlanPricesViewContent({ organizationId }: { organizationId: string }) {
  const { data: organization } = useOrganization(organizationId);

  const { data: plans, isLoading } =
    usePhonePlansByOrganizationId(organizationId);

  if (isLoading) {
    return (
      <div className="container py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">
            Chargement des forfaits...
          </p>
        </div>
      </div>
    );
  }

  const columns = createColumns({ organizationId });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Gestion des prix des forfaits
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez les prix de base et personnalisés des forfaits pour{' '}
            <span className="font-bold text-gray-900 dark:text-gray-100">
              {organization?.name}
            </span>
          </p>
        </div>
      </div>

      <Card className="group hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 border-0 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-800/50 overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            Forfaits disponibles
          </CardTitle>
        </CardHeader>
        <CardContent>
          {plans && plans.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-muted-foreground text-6xl mb-4">📱</div>
              <p className="text-muted-foreground text-lg">
                Aucun forfait disponible
              </p>
            </div>
          ) : (
            <DataTable columns={columns} data={plans || []} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
